import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { CsvAsStringArrays } from '@malou-io/package-utils';

import { PostInsight } from '#modules/posts/models/post-insight';
import { AbstractCsvService } from '#modules/statistics/components/download-insights-modal/models/csv-service.abstract';
import { StatisticsStore } from '#modules/statistics/stores/store/statistics.store';
import { CSV_NUMBER_LOCALE } from '#shared/helpers/create-csv-string';
import { formatShortNumber } from '#shared/helpers/format-number';
import { EnumTranslatePipe } from '#shared/pipes/enum-translate.pipe';

interface PublicationsData {
    postsWithInsights: PostInsight[];
}

@Injectable()
export class PublicationsCsvInsightsV3Service extends AbstractCsvService<PublicationsData> {
    private readonly _statisticsStore = inject(StatisticsStore);
    private readonly _enumTranslatePipe = inject(EnumTranslatePipe);

    protected override _getData$(): Observable<PublicationsData> {
        return of({
            postsWithInsights: this._statisticsStore.postsWithInsightsV2Data(),
        });
    }

    protected override _getCsvHeaderRow(): CsvAsStringArrays[0] {
        return ['Date', 'Type', 'Caption', 'Platform', 'Impressions', 'Likes', 'Comments', 'Shares', 'Saves', 'Engagement Rate'];
    }

    protected override _getCsvDataRows({ postsWithInsights }: PublicationsData): CsvAsStringArrays {
        return postsWithInsights.map((postInsight) => {
            const postDate = new Date(postInsight.postCreatedAt).toLocaleDateString();
            const type = postInsight.postType;
            const caption = postInsight.caption ?? '';
            const platform = this._enumTranslatePipe.transform(postInsight.platformKey, 'platform_key');
            const impressions = postInsight.impressions?.toString() ?? '';
            const likes = postInsight.likes?.toString() ?? '0';
            const comments = postInsight.comments?.toString() ?? '0';
            const shares = postInsight.shares?.toString() ?? '0';
            const saves = postInsight.saved?.toString() ?? '0';
            const engagementRate =
                typeof postInsight.engagementRate === 'number' ? formatShortNumber(postInsight.engagementRate, CSV_NUMBER_LOCALE) : '';

            return [postDate, type, caption, platform, impressions, likes, comments, shares, saves, engagementRate];
        });
    }
}
